import type { Viewport } from '../store/types';

export const ZOOM_MIN = 0.2;
export const ZOOM_MAX = 4;

export function worldToScreen(p: { x: number; y: number }, vp: Viewport): { x: number; y: number } {
  return { x: p.x * vp.zoom + vp.pan.x, y: p.y * vp.zoom + vp.pan.y };
}

export function screenToWorld(p: { x: number; y: number }, vp: Viewport): { x: number; y: number } {
  return { x: (p.x - vp.pan.x) / vp.zoom, y: (p.y - vp.pan.y) / vp.zoom };
}

export function clampZoom(z: number): number {
  return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z));
}

export function zoomAtCursor(vp: Viewport, cursor: { x: number; y: number }, nextZoom: number): Viewport {
  const z = clampZoom(nextZoom);
  const world = screenToWorld(cursor, vp);
  const pan = { x: cursor.x - world.x * z, y: cursor.y - world.y * z };
  return { pan, zoom: z };
}

// ---- Gesture smoothing ----
// How fast a unit of wheel delta translates to zoom. Higher = faster.
export const ZOOM_SENSITIVITY = 0.01;
// Per-event zoom factor is clamped to this range so a single fast wheel tick or
// flick can't teleport the view. Smooth trackpad pinches stay well inside it;
// only big mouse-wheel notches hit the clamp, giving a controlled step.
export const MAX_ZOOM_STEP = 1.15;
// Wheel deltaMode line/page → approximate pixels.
const LINE_PX = 16;
const PAGE_PX = 800;

function deltaToPixels(delta: number, mode: number): number {
  if (mode === 1) return delta * LINE_PX; // DOM_DELTA_LINE
  if (mode === 2) return delta * PAGE_PX; // DOM_DELTA_PAGE
  return delta; // DOM_DELTA_PIXEL
}

export interface WheelInput {
  deltaX: number;
  deltaY: number;
  deltaMode: number;
  /** macOS trackpad pinch sets ctrlKey; explicit ctrl/cmd+wheel also zooms. */
  ctrlKey: boolean;
  metaKey: boolean;
  cursor: { x: number; y: number };
}

/**
 * Map one wheel event to the next viewport, design-tool style:
 *   - pinch / ctrl|cmd+wheel → smooth zoom anchored at the cursor
 *   - plain two-finger swipe → pan (matches Figma/Sketch trackpad behavior)
 */
export function wheelToViewport(vp: Viewport, ev: WheelInput): Viewport {
  const dx = deltaToPixels(ev.deltaX, ev.deltaMode);
  const dy = deltaToPixels(ev.deltaY, ev.deltaMode);

  if (ev.ctrlKey || ev.metaKey) {
    const raw = Math.exp(-dy * ZOOM_SENSITIVITY);
    const factor = Math.max(1 / MAX_ZOOM_STEP, Math.min(MAX_ZOOM_STEP, raw));
    return zoomAtCursor(vp, ev.cursor, vp.zoom * factor);
  }

  // Two-finger swipe: pan. Content follows the fingers, so subtract delta.
  return { zoom: vp.zoom, pan: { x: vp.pan.x - dx, y: vp.pan.y - dy } };
}

/**
 * Combined pan + pinch-zoom for a two-pointer touch gesture. `prev`/`next` are
 * the two-finger centroid + spread between successive pointer-move samples.
 */
export function pinchToViewport(
  vp: Viewport,
  prev: { center: { x: number; y: number }; distance: number },
  next: { center: { x: number; y: number }; distance: number },
): Viewport {
  // Pan by centroid movement first.
  const panned: Viewport = {
    zoom: vp.zoom,
    pan: {
      x: vp.pan.x + (next.center.x - prev.center.x),
      y: vp.pan.y + (next.center.y - prev.center.y),
    },
  };
  // Then zoom by the spread ratio, anchored at the current centroid.
  if (prev.distance <= 0) return panned;
  const ratio = next.distance / prev.distance;
  return zoomAtCursor(panned, next.center, panned.zoom * ratio);
}
